import { genId } from './ids.js';
const HEADING_RE = /^#\s+(.*)$/;
const BRACKET_RE = /^\[(.*)\]$/;
/** Single-word stage cues addressed to the presenter (vs production markers). */
const DIRECTION_WORDS = new Set([
    'PAUSE',
    'SMILE',
    'BREATHE',
    'BEAT',
    'SLOW',
    'SLOW DOWN',
    'LOOK UP',
    'STOP',
]);
function classifyBracket(inner) {
    const upper = inner.toUpperCase();
    if (!inner.includes(':') && DIRECTION_WORDS.has(upper))
        return 'direction';
    return 'marker';
}
function classifyParagraph(text) {
    return text.trim().endsWith('?') ? 'question' : 'paragraph';
}
export function parseScript(text, title = 'Untitled script') {
    const now = Date.now();
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const sections = [];
    let currentHeading = '';
    let currentBlocks = [];
    let pending = [];
    const flushParagraph = () => {
        if (pending.length === 0)
            return;
        const body = pending.join('\n').replace(/\s+$/, '');
        if (body.trim() !== '') {
            currentBlocks.push({ id: genId('blk'), type: classifyParagraph(body), text: body });
        }
        pending = [];
    };
    const closeSection = () => {
        flushParagraph();
        if (currentBlocks.length > 0 || currentHeading !== '') {
            sections.push({ id: genId('sec'), heading: currentHeading, blocks: currentBlocks });
        }
        currentHeading = '';
        currentBlocks = [];
    };
    for (const raw of lines) {
        const line = raw.replace(/\s+$/, '');
        const trimmed = line.trim();
        const heading = trimmed.match(HEADING_RE);
        if (heading) {
            // A new heading starts a new section (unless we are still on the empty opener).
            if (currentBlocks.length > 0 || currentHeading !== '') {
                closeSection();
            }
            else {
                flushParagraph();
            }
            currentHeading = heading[1].trim();
            continue;
        }
        const bracket = trimmed.match(BRACKET_RE);
        if (bracket) {
            flushParagraph();
            const inner = bracket[1].trim();
            currentBlocks.push({ id: genId('blk'), type: classifyBracket(inner), text: inner });
            continue;
        }
        if (trimmed === '') {
            flushParagraph();
            continue;
        }
        pending.push(line);
    }
    closeSection();
    if (sections.length === 0) {
        sections.push({ id: genId('sec'), heading: '', blocks: [] });
    }
    return {
        id: genId('proj'),
        title,
        description: '',
        sections,
        version: 1,
        createdAt: now,
        modifiedAt: now,
    };
}
export function serializeScript(project) {
    const parts = [];
    for (const section of project.sections) {
        if (section.heading.trim() !== '')
            parts.push(`# ${section.heading.trim()}`);
        for (const block of section.blocks) {
            if (block.type === 'marker' || block.type === 'direction') {
                parts.push(`[${block.text}]`);
            }
            else {
                parts.push(block.text);
            }
        }
    }
    return parts.join('\n\n').trim() + '\n';
}
/** Convenience: flatten every block across sections, in reading order. */
export function flattenBlocks(project) {
    return project.sections.flatMap((s) => s.blocks);
}
/**
 * Re-use ids from `prev` for blocks/sections in `next` whose content is unchanged.
 * Parsing regenerates ids every keystroke; without this the presenter's reading
 * anchor (a block id) would vanish on every edit and jump to the top. Reusing ids
 * for unchanged blocks keeps the reading position stable during live editing.
 */
export function reconcileIds(prev, next) {
    if (!prev)
        return next;
    const pool = new Map();
    for (const b of flattenBlocks(prev)) {
        const key = `${b.type} ${b.text}`;
        const list = pool.get(key);
        if (list)
            list.push(b.id);
        else
            pool.set(key, [b.id]);
    }
    const sections = next.sections.map((section, i) => ({
        ...section,
        id: prev.sections[i]?.id ?? section.id,
        blocks: section.blocks.map((block) => {
            const key = `${block.type} ${block.text}`;
            const reuse = pool.get(key);
            const id = reuse && reuse.length > 0 ? reuse.shift() : block.id;
            return { ...block, id };
        }),
    }));
    return { ...next, id: prev.id, createdAt: prev.createdAt, sections };
}
//# sourceMappingURL=parser.js.map